import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import RefreshIcon from '@mui/icons-material/Refresh'
import api from 'services/api'
import { usePlayer } from '../Moments/MomentsBrowser'
import ReportCard, { useReportActions, PlayerAudio } from './ReportCard'

// The Reports page (audio-streaming step 6b, step-6-social.md "Reports" and
// "Admin"): every report a person filed, of every kind, one status at a time,
// newest first. Open is the queue; actioned and dismissed are what was done.
// Nothing here acts on its own and nothing acts on more than one report: each
// action is one press on one report, and writes who pressed it and when. A
// report opens on its own page, which is what the Slack post links to.

const PAGE = 50

const STATUSES = [
  { value: 'open', label: 'Open' },
  { value: 'actioned', label: 'Actioned' },
  { value: 'dismissed', label: 'Dismissed' },
]

const Reports = () => {
  const navigate = useNavigate()
  const [status, setStatus] = useState('open')
  const [rows, setRows] = useState([])
  const [nextBefore, setNextBefore] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const player = usePlayer()

  const load = useCallback(
    async (before) => {
      setLoading(true)
      try {
        const data = await api.reports.listReports({
          status,
          limit: PAGE,
          before,
        })
        setRows((current) =>
          before ? [...current, ...data.reports] : data.reports
        )
        setNextBefore(data.next_before)
        setError(null)
      } catch (err) {
        setError(err.message)
      } finally {
        setLoading(false)
      }
    },
    [status]
  )

  useEffect(() => {
    load(null)
  }, [load])

  // Only an open report has actions, and once acted on, or found closed by a
  // conflict, it is not open: it leaves the list. A disabled quote is the same
  // quote on any other report of it; a deleted account takes every report about
  // it with it, and is a deleted account wherever it reported someone.
  const onSettled = useCallback((report, action, row) => {
    setRows((current) => {
      let next = current.filter((r) => r.id !== report.id)
      if (action === 'disable_moment' && row?.moment) {
        next = next.map((r) =>
          r.moment?.id === row.moment.id ? { ...r, moment: row.moment } : r
        )
      }
      if (action === 'disable_account' && report.reported) {
        const gone = report.reported.id
        next = next
          .filter((r) => r.reported?.id !== gone)
          .map((r) =>
            r.reporter?.id === gone
              ? { ...r, reporter: { ...r.reporter, deleted: true } }
              : r
          )
      }
      return next
    })
  }, [])
  const actions = useReportActions(onSettled)

  const summary = useMemo(() => {
    const quotes = rows.filter((r) => r.target_kind === 'moment').length
    const people = rows.filter((r) => r.target_kind === 'user').length
    const comments = rows.filter((r) => r.target_kind === 'comment').length
    return `${rows.length}${
      nextBefore ? '+' : ''
    } ${status} · ${quotes} quotes · ${people} people · ${comments} comments`
  }, [rows, nextBefore, status])

  return (
    <Box
      sx={{
        px: { mobile: 2, desktop: 6 },
        py: { mobile: 3, desktop: 4 },
        maxWidth: 1200,
        mx: 'auto',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 1,
        }}
      >
        <Typography variant="h5" fontWeight={700}>
          Reports
        </Typography>
        <ToggleButtonGroup
          value={status}
          exclusive
          size="small"
          onChange={(event, next) => next && setStatus(next)}
        >
          {STATUSES.map(({ value, label }) => (
            <ToggleButton key={value} value={value}>
              {label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        {loading && <CircularProgress size={18} />}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {summary}
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load(null)}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Every report a person filed — a friend’s quote, a person, and from step
        6c a comment — newest first. Each action is one press on one report;
        none runs on its own. Click a report for its own page, the one its Slack
        post links to.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {!loading && !error && rows.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ my: 3 }}>
          No {status} reports.
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {rows.map((report) => (
          <ReportCard
            key={report.id}
            report={report}
            player={player}
            onOpen={() => navigate(`/reports/${report.id}`)}
            onAct={actions.request}
            busy={actions.busy[report.id]}
          />
        ))}
      </Box>
      {nextBefore && (
        <Button
          sx={{ mt: 2 }}
          onClick={() => load(nextBefore)}
          disabled={loading}
        >
          Load more
        </Button>
      )}
      {/* One element for the whole page: a second play stops the first. */}
      <PlayerAudio player={player} />
      {actions.dialog}
    </Box>
  )
}

export default Reports
